import React, { useState, useEffect, useContext, useRef } from 'react';
import {
  CircularProgress,
  Flex,
  Heading,
  Text,
  useToast,
} from '@chakra-ui/react';
import { useParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { ModalDialog } from '../../components/ModalDialog';
import { InterviewQuestionForm } from '../InterviewQuestion/InterviewQuestionForm';
import { SaveCancelButtons } from './SaveCancelButtons';
import { useGetMetadata } from '../../hooks/useGetMetadata';
import {
  getInterviewResult,
  getInterviewSheet,
  putInterviewResultsAnswers,
} from '../../api';
import { GetInterviewResult } from '../../types/interviewResultConfirmApiTypes';
import {
  SetInterviewResultContext,
  InterviewResultContext,
} from '../../contexts/InterviewResultContext';
import { SetQuestionContext } from '../../contexts/QuestionContext';
import { SetQuestionItemContext } from '../../contexts/QuestionItemContext';
import { SetConditionContext } from '../../contexts/ConditionContext';
import { SetPrerequisiteContext } from '../../contexts/PrerequisiteContext';
import {
  ADD_INTERVIEW_RESULT,
  CLEAR_INTERVIEW_RESULTS,
  CHANGE_ANSWER_ITEMS,
  CHANGE_GUEST_INFO,
} from '../../actions/interviewResultAction';
import { GET_INTERVIEWSHEET } from '../../actions/questionAction';
import { GET_INTERVIEWSHEET as GET_INTERVIEWSHEET_QUESTION_ITEM } from '../../actions/questionItemAction';
import { GET_INTERVIEWSHEET as GET_INTERVIEWSHEET_CONDITION } from '../../actions/conditionAction';
import { GET_INTERVIEWSHEET as GET_INTERVIEWSHEET_PREREQUISITE } from '../../actions/prerequisiteAction';
import { GetInterviewSheet } from '../../types/interviewSheetApiTypes';
import { toQuestions } from '../../domains/toQuestions';
import { toQuestionItems } from '../../domains/toQuestionItems';
import { toConditions } from '../../domains/toConditions';
import { toPrerequisite } from '../../domains/toPrerequisite';
import { toPutInterviewResultsAnswers } from '../../domains/toInterviewResultsAnswers';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  save: {
    readonly on: () => void;
    readonly off: () => void;
    readonly toggle: () => void;
  };
};

export const QuestionEdit: React.VFC<Props> = ({ isOpen, onClose, save }) => {
  const [loading1, setLoading1] = useState(false);
  const [loading2, setLoading2] = useState(false);
  const [answersChanged, setAnswersChanged] = useState<boolean>(false);
  const loading = loading1 || loading2;
  const { id } = useParams<{ id: string }>();
  const { user, getAccessTokenSilently } = useAuth0();
  const { auth0UserData } = useGetMetadata();
  const { tenantId } = auth0UserData;
  const userId = user?.sub ?? '';
  const userName = encodeURI(user?.name ?? '');
  const [interviewSheetId, setInterviewSheetId] = useState('');
  const toast = useToast();
  const { state: resultState } = useContext(InterviewResultContext);
  const { dispatch: resultDispatch } = useContext(SetInterviewResultContext);
  const { dispatch: questionDispatch } = useContext(SetQuestionContext);
  const { dispatch: questionItemDispatch } = useContext(SetQuestionItemContext);
  const { dispatch: conditionDispatch } = useContext(SetConditionContext);
  const { dispatch: prerequisiteDispatch } = useContext(SetPrerequisiteContext);

  const formRef = useRef<HTMLFormElement>(null);
  const resultStateRef = useRef(resultState);

  useEffect(() => {
    resultStateRef.current = resultState;
  }, [resultState]);

  const handleOnClose = () => {
    onClose();
  };

  const waitForStateUpdate = (): Promise<void> => {
    return new Promise((resolve) => {
      const interval = setInterval(() => {
        if (resultStateRef.current !== resultState) {
          clearInterval(interval);
          resolve();
        }
      }, 50); // 状態が更新されるまで50msごとにチェック
    });
  };

  const handleSave = async () => {
    if (formRef.current) {
      formRef.current.submit();
    }

    await waitForStateUpdate();

    const putData = async (): Promise<void> => {
      try {
        setLoading1(true);

        const getAccessToken = await getAccessTokenSilently({
          audience: 'https://www.webinterview.mic.jp/',
          scope: 'read:interview-results',
        });

        const status = await getInterviewResult(
          tenantId,
          id,
          userId,
          userName,
          getAccessToken,
        );

        if (status.ok) {
          const resultData: GetInterviewResult = await status.json();

          const updateAccessToken = await getAccessTokenSilently({
            audience: 'https://www.webinterview.mic.jp/',
            scope: 'update:interview-answers',
          });

          const result = await putInterviewResultsAnswers(
            tenantId,
            id,
            userId,
            userName,
            updateAccessToken,
            toPutInterviewResultsAnswers(
              resultStateRef.current[0],
              resultData.version,
            ),
          );

          if (result.ok) {
            toast({
              title: '保存完了',
              description: '問診の変更内容を保存しました',
              status: 'success',
              duration: 5000,
              isClosable: true,
            });
            save.toggle();
          } else {
            throw new Error(result.status.toString());
          }
        } else {
          throw new Error(status.status.toString());
        }
      } catch (error) {
        toast({
          title: `エラー {${error}}`,
          description: (
            <>
              <Text>エラーが発生しました。</Text>
              <Text>恐れ入りますが、再度操作をやり直してください。</Text>
            </>
          ),
          status: 'error',
          duration: 8000,
          isClosable: true,
        });
        console.error({ error });
        onClose();
      } finally {
        setLoading1(false);
      }
    };

    putData();
  };

  useEffect(() => {
    const getData = async (): Promise<void> => {
      try {
        setLoading1(true);

        const accessToken = await getAccessTokenSilently({
          audience: 'https://www.webinterview.mic.jp/',
          scope: 'read:interview-results',
        });

        const status = await getInterviewResult(
          tenantId,
          id,
          userId,
          userName,
          accessToken,
        );

        if (status.ok) {
          const data: GetInterviewResult = await status.json();

          setInterviewSheetId(data.interview_sheet_id);

          resultDispatch({ type: CLEAR_INTERVIEW_RESULTS });

          resultDispatch({
            type: ADD_INTERVIEW_RESULT,
            interviewResultId: id,
            interviewSheetId: data.interview_sheet_id,
            interviewSheetHistoryId: data.interview_sheet_history_id,
            interviewSheetName: '',
          });

          resultDispatch({
            type: CHANGE_GUEST_INFO,
            interviewResultId: id,
            isNew: false,
            pId: data.pid,
            firstName: data.first_name,
            lastName: data.last_name,
            firstKana: data.first_kana,
            lastKana: data.last_kana,
            sex: data.sex,
            birthDay: data.birthday,
            zipCode: data.zip_code,
            streetAddress: data.street_address,
            phoneNumber: data.phone_number,
            mailAddress: data.mail_address,
            reservationDate: data.reservation_date,
          });

          resultDispatch({
            type: CHANGE_ANSWER_ITEMS,
            interviewResultId: id,
            answers: data.answers.map((a) => ({
              answerId: a.id,
              questionId: a.question_id,
              answerItems: a.answer_items.map((ai) => ({
                answerItemId: ai.id,
                questionItemId: ai.question_item_id,
                description: ai.description,
                checked: ai.checked,
                choiceNotes: ai.choice_notes,
              })),
            })),
          });
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading1(false);
      }
    };

    if (isOpen) {
      getData();
    }
  }, [
    isOpen,
    tenantId,
    id,
    userId,
    userName,
    resultDispatch,
    getAccessTokenSilently,
  ]);

  useEffect(() => {
    const getData = async (): Promise<void> => {
      try {
        setLoading2(true);

        const status = await getInterviewSheet(tenantId, interviewSheetId);

        if (status.ok) {
          const data: GetInterviewSheet = await status.json();

          questionDispatch({
            type: GET_INTERVIEWSHEET,
            data: toQuestions(data),
          });

          questionItemDispatch({
            type: GET_INTERVIEWSHEET_QUESTION_ITEM,
            data: toQuestionItems(data),
          });

          conditionDispatch({
            type: GET_INTERVIEWSHEET_CONDITION,
            data: toConditions(data),
          });

          prerequisiteDispatch({
            type: GET_INTERVIEWSHEET_PREREQUISITE,
            data: toPrerequisite(data),
          });
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading2(false);
      }
    };

    if (isOpen && interviewSheetId) {
      getData();
    }
  }, [
    isOpen,
    tenantId,
    interviewSheetId,
    questionDispatch,
    questionItemDispatch,
    conditionDispatch,
    prerequisiteDispatch,
  ]);

  useEffect(() => {
    if (formRef.current && isOpen && loading) {
      formRef.current.reset();
      console.log('Form reference:', formRef.current);
    }
  }, [loading, isOpen]); // loading

  return (
    <ModalDialog
      header={
        loading ? (
          <></>
        ) : (
          <Heading
            size="lg"
            color="gray.600"
            textAlign="left"
            pb="4"
            borderBottomWidth="1px"
          >
            問診結果修正
          </Heading>
        )
      }
      body={
        loading ? (
          <Flex w="full" h="lg" justifyContent="center" alignItems="center">
            <CircularProgress isIndeterminate color="blue.300" />
          </Flex>
        ) : (
          <InterviewQuestionForm
            handleNext={handleOnClose}
            handlePrevious={handleOnClose}
            isEditMode={true}
            setAnswersChanged={setAnswersChanged}
            ref={formRef}
          ></InterviewQuestionForm>
        )
      }
      footer={
        loading ? (
          <></>
        ) : (
          <SaveCancelButtons
            onSave={handleSave}
            onCancel={handleOnClose}
            isDisabled={!answersChanged || loading}
          />
        )
      }
      isOpen={isOpen}
      onClose={handleOnClose}
      size="2xl"
    ></ModalDialog>
  );
};
